/**
 * 日付処理のユーティリティ関数
 * フロントエンド全体でタイムゾーン処理を統一するために使用します
 */

// アプリケーション全体で使用するタイムゾーン
export const APP_TIMEZONE = 'Asia/Tokyo';
export const TIMEZONE_OFFSET = 9; // 日本時間は UTC+9

/**
 * UTC日付文字列を日本時間の日付文字列に変換する
 * @param {string} utcDateStr - UTC日付文字列（ISO形式）
 * @returns {string} - YYYY-MM-DD形式の日付文字列
 */
export function formatDateToJST(utcDateStr) {
  if (!utcDateStr) return '';
  
  try {
    // 日付オブジェクトを作成
    const date = new Date(utcDateStr);
    if (isNaN(date.getTime())) {
      console.error('無効な日付:', utcDateStr);
      return '';
    }
    
    // 日本時間のオプションを指定
    const options = {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      timeZone: 'Asia/Tokyo'
    };
    
    // 日本時間でフォーマット
    const jstDateStr = date.toLocaleDateString('ja-JP', options)
      .replace(/\//g, '-')  // スラッシュをハイフンに置換
      .replace(/(\d+)-(\d+)-(\d+)/, '$1-$2-$3'); // 年月日の順序を確認
    
    return jstDateStr;
  } catch (error) {
    console.error('日付変換エラー:', error);
    return '';
  }
}

/**
 * データベースから取得した日付文字列を日本時間の日付文字列に変換する
 * UTC日付文字列やタイムスタンプを正しく日本時間に変換する
 * @param {string} dbDateStr - データベースの日付文字列（ISO形式またはYYYY-MM-DD形式）
 * @returns {string} - YYYY-MM-DD形式の日付文字列
 */
export function formatDBDateToJST(dbDateStr) {
  if (!dbDateStr) return '';
  
  try {
    // まず日付文字列から日付部分のみを抽出（YYYY-MM-DD）
    const datePart = dbDateStr.split('T')[0].split(' ')[0];
    
    // 日付が既にYYYY-MM-DD形式で、タイムゾーン情報がない場合はそのまま返す
    if (/^\d{4}-\d{2}-\d{2}$/.test(datePart) && !dbDateStr.includes('T') && !dbDateStr.includes('Z')) {
      return datePart;
    }
    
    // ISO形式やタイムスタンプの場合は、formatDateToJSTを使用して正しく変換
    return formatDateToJST(dbDateStr);
  } catch (error) {
    console.error('DB日付変換エラー:', error, 'Input:', dbDateStr);
    return '';
  }
}

/**
 * UTC日付文字列を日本時間の時間文字列に変換する
 * @param {string} utcDateStr - UTC日付文字列（ISO形式）
 * @param {boolean} includeSeconds - 秒を含めるかどうか
 * @returns {string} - HH:MM:SS形式またはHH:MM形式の時間文字列
 */
export function formatTimeToJST(utcDateStr, includeSeconds = false) {
  if (!utcDateStr) return '';
  
  try {
    // 日付オブジェクトを作成
    const date = new Date(utcDateStr);
    if (isNaN(date.getTime())) {
      console.error('無効な日付:', utcDateStr);
      return '';
    }
    
    // 日本時間のオプションを指定
    const options = {
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'Asia/Tokyo',
      hour12: false
    };
    
    if (includeSeconds) {
      options.second = '2-digit';
    }
    
    // 日本時間でフォーマット
    const jstTimeStr = date.toLocaleTimeString('ja-JP', options);
    
    // toLocaleTimeString の結果を返す
    return jstTimeStr;
  } catch (error) {
    console.error('時間変換エラー:', error);
    return '';
  }
}

/**
 * 日本時間の日付と時間からUTC日付オブジェクトを作成する
 * @param {string} dateStr - YYYY-MM-DD形式の日付文字列
 * @param {string} timeStr - HH:MM:SS形式またはHH:MM形式の時間文字列
 * @returns {Date} - UTC形式の日付オブジェクト
 */
export function createUTCDate(dateStr, timeStr) {
  try {
    // 時間文字列に秒がない場合は追加
    if (timeStr && timeStr.split(':').length === 2) {
      timeStr = `${timeStr}:00`;
    }
    
    // 日本時間の日付時間文字列を作成
    const jstDateTimeStr = `${dateStr}T${timeStr || '00:00:00'}+09:00`;
    
    // ISO形式の文字列からDateオブジェクトを作成
    // これにより自動的にUTCに変換される
    const date = new Date(jstDateTimeStr);
    
    if (isNaN(date.getTime())) {
      console.error('無効な日付または時間:', dateStr, timeStr);
      return null;
    }
    
    return date;
  } catch (error) {
    console.error('日付作成エラー:', error);
    return null;
  }
}

/**
 * 現在の日本時間の日付を取得する
 * @returns {string} - YYYY-MM-DD形式の日付文字列
 */
export function getCurrentJSTDate() {
  const now = new Date();
  
  // 日本時間のオプションを指定
  const options = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    timeZone: 'Asia/Tokyo'
  };
  
  // 日本時間でフォーマット
  const jstDateStr = now.toLocaleDateString('ja-JP', options)
    .replace(/\//g, '-')  // スラッシュをハイフンに置換
    .replace(/(\d+)-(\d+)-(\d+)/, '$1-$2-$3'); // 年月日の順序を確認
  
  return jstDateStr;
}

/**
 * 現在の日本時間の時間を取得する
 * @param {boolean} includeSeconds - 秒を含めるかどうか
 * @returns {string} - HH:MM:SS形式またはHH:MM形式の時間文字列
 */
export function getCurrentJSTTime(includeSeconds = false) {
  const now = new Date();
  
  // 日本時間のオプションを指定
  const options = {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Asia/Tokyo',
    hour12: false
  };
  
  if (includeSeconds) {
    options.second = '2-digit';
  }
  
  // 日本時間でフォーマット
  const jstTimeStr = now.toLocaleTimeString('ja-JP', options);
  return jstTimeStr;
}

/**
 * 日本時間の月の開始日を取得する
 * @param {string} yearMonth - YYYY-MM形式の年月文字列
 * @returns {string} - YYYY-MM-DD形式の日付文字列
 */
export function getMonthStartDate(yearMonth) {
  return `${yearMonth}-01`;
}

/**
 * 日本時間の月の終了日を取得する
 * @param {string} yearMonth - YYYY-MM形式の年月文字列
 * @returns {string} - YYYY-MM-DD形式の日付文字列
 */
export function getMonthEndDate(yearMonth) {
  const [year, month] = yearMonth.split('-').map(Number);
  const lastDay = new Date(year, month, 0).getDate(); // 月の最終日
  return `${yearMonth}-${String(lastDay).padStart(2, '0')}`;
}

/**
 * 日付文字列をフォーマットする（YYYY-MM-DD → YYYY年MM月DD日）
 * @param {string} dateStr - YYYY-MM-DD形式の日付文字列
 * @returns {string} - YYYY年MM月DD日形式の日付文字列
 */
export function formatDateJP(dateStr) {
  if (!dateStr) return '';
  
  const [year, month, day] = dateStr.split('-');
  return `${year}年${Number(month)}月${Number(day)}日`;
}

/**
 * 日付が週末かどうかを判定する
 * @param {string} dateStr - YYYY-MM-DD形式の日付文字列
 * @returns {boolean} - 週末の場合はtrue、それ以外はfalse
 */
export function isWeekend(dateStr) {
  const date = new Date(dateStr);
  const day = date.getDay();
  return day === 0 || day === 6; // 0: 日曜日, 6: 土曜日
}

/**
 * 月の日数を取得する
 * @param {string} yearMonth - YYYY-MM形式の年月文字列
 * @returns {number} - 月の日数
 */
export function getDaysInMonth(yearMonth) {
  const [year, month] = yearMonth.split('-').map(Number);
  return new Date(year, month, 0).getDate();
}

/**
 * 月の全日付を生成する
 * @param {string} yearMonth - YYYY-MM形式の年月文字列
 * @returns {Array<string>} - YYYY-MM-DD形式の日付文字列の配列
 */
export function generateDatesForMonth(yearMonth) {
  const daysInMonth = getDaysInMonth(yearMonth);
  const dates = [];
  
  for (let i = 1; i <= daysInMonth; i++) {
    const day = String(i).padStart(2, '0');
    dates.push(`${yearMonth}-${day}`);
  }
  
  return dates;
}

/**
 * 日付文字列からタイムゾーンに依存しない日付オブジェクトを作成する
 * ローカルタイムゾーンに依存せず、常に同じ結果を返す
 * @param {string} dateStr - YYYY-MM-DD形式の日付文字列
 * @returns {Date} - UTC日付オブジェクト（ただし日付部分のみ）
 */
export function createSafeDate(dateStr) {
  if (!dateStr) return new Date();
  
  try {
    const [year, month, day] = dateStr.split('-').map(Number);
    
    // UTC日付として作成（タイムゾーンに依存しない）
    const date = new Date(Date.UTC(year, month - 1, day));
    
    if (isNaN(date.getTime())) {
      console.error('無効な日付:', dateStr);
      return new Date();
    }
    
    return date;
  } catch (error) {
    console.error('日付作成エラー:', error, 'Input:', dateStr);
    return new Date();
  }
}

/**
 * 日付文字列から曜日を取得する（タイムゾーンに依存しない）
 * @param {string} dateStr - YYYY-MM-DD形式の日付文字列
 * @returns {string} - 曜日（短縮形：日、月、火、etc.）
 */
export function getDayOfWeek(dateStr) {
  const date = createSafeDate(dateStr);
  const dayNames = ['日', '月', '火', '水', '木', '金', '土'];
  return dayNames[date.getUTCDay()];
} 